import { isDefined } from "@bps/common-utils";
import { compareDatesPredicate } from "@bps/date-time";
import { AppointmentVisitDto } from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  AccClinicalTab,
  PermanentClinicalTab
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ProviderTypeCode } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { ClaimFormLabels } from "@modules/acc/screens/claim/components/ClaimFormEnums.ts";
import { DischargeStatus } from "@shared-types/clinical/discharge-status.enum.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import {
  unlinkEpisodeOfCareFromEncounter,
  updateEncounterEpisodeOfCareId
} from "@stores/acc/utils/acc.utils.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { RootStore } from "@stores/root/RootStore.ts";

export class ConditionsSidePanelHelper {
  constructor(
    private _clinicalRecord: ClinicalRecord,
    private _root: RootStore,
    private setTabs: () => Promise<void>
  ) {}

  private _encounterArray: Encounter[] = [];
  private _injuryTabId: string;

  private get core() {
    return this._root.core;
  }

  private get acc() {
    return this._root.acc;
  }

  private get clinical() {
    return this._root.clinical;
  }

  get clinicalRecord() {
    return this._clinicalRecord;
  }

  getList = (
    episodesOfCare: EpisodeOfCare[],
    showOnHoldSection: boolean,
    clinicalRecordDischargeStatus?: string | undefined
  ) => {
    let episodesOfCareList = Array.from(episodesOfCare);

    let linkedConditionModel: ConditionDetailsModel | undefined;

    if (this.hasLinkedCondition) {
      const linkedCondition = episodesOfCareList.find(x =>
        this.isLinkedEpisodeOfCare(x.id)
      );

      if (linkedCondition) {
        linkedConditionModel = this.toConditionDetailsModel(linkedCondition);

        // Remove the linked from the conditions
        episodesOfCareList = episodesOfCareList.filter(
          x => !this.isLinkedEpisodeOfCare(x.id)
        );
      }
    }

    episodesOfCareList.sort((a, b) =>
      compareDatesPredicate(a.sortDate, b.sortDate, true)
    );

    const onHoldConditions = episodesOfCareList
      .filter(c => c.claim?.isOnHold)
      .map(c => this.toConditionDetailsModel(c));

    const inProgressConditions = episodesOfCareList
      .filter(c => !c.discharged && !c.claim?.isOnHold)
      .map(c => this.toConditionDetailsModel(c));

    const dischargedConditions = episodesOfCareList
      .filter(c => c.discharged)
      .map(c => this.toConditionDetailsModel(c));

    // Add the linked item to the front of the list.
    if (linkedConditionModel) {
      if (
        showOnHoldSection &&
        linkedConditionModel.episodeOfCare?.claim?.isOnHold
      ) {
        onHoldConditions.unshift(linkedConditionModel);
      } else {
        linkedConditionModel.episodeOfCare?.discharged &&
        clinicalRecordDischargeStatus !== DischargeStatus.Reversed
          ? dischargedConditions.unshift(linkedConditionModel)
          : inProgressConditions.unshift(linkedConditionModel);
      }
    }

    if (inProgressConditions.length === 0) {
      inProgressConditions.push(this.emptyModel("No in progress conditions"));
    }

    if (dischargedConditions.length === 0) {
      dischargedConditions.push(this.emptyModel("No discharged conditions"));
    }

    if (onHoldConditions.length === 0) {
      onHoldConditions.push(this.emptyModel("No on hold conditions"));
    }

    const groups = [
      {
        key: "inProgress",
        name: "In progress",
        count: inProgressConditions.length,
        startIndex: 0
      },
      {
        key: "discharged",
        name: "Discharged",
        count: dischargedConditions.length,
        startIndex: inProgressConditions.length
      }
    ];

    if (showOnHoldSection) {
      groups.push({
        key: "onHold",
        name: "On hold",
        count: onHoldConditions.length,
        startIndex: dischargedConditions.length + inProgressConditions.length
      });
    }

    return {
      items: [
        ...inProgressConditions,
        ...dischargedConditions,
        ...(showOnHoldSection ? onHoldConditions : [])
      ],
      groups
    };
  };

  public toConditionDetailsModel(
    episodeOfCare: EpisodeOfCare
  ): ConditionDetailsModel {
    return {
      episodeOfCare,
      diagnosis: episodeOfCare.primaryDiagnosis
        ? episodeOfCare.primaryDiagnosis
        : ClaimFormLabels.undiagnosed,
      referralIn: episodeOfCare.isReferral,
      apptRemaining: episodeOfCare.claim?.totalRemainingAppointments
        ? `${episodeOfCare.claim?.totalRemainingAppointments} consults remain`
        : undefined,
      initialConsultDate:
        episodeOfCare.claim?.initialConsultDate?.toDayDefaultFormat(),
      injuryDate: episodeOfCare.injuryDate?.toDayDefaultFormat(),
      side: episodeOfCare.diagnosisSide,
      appointmentVisits: episodeOfCare.claim?.appointmentVisits
        ? episodeOfCare.claim.appointmentVisits.filter(av =>
            isDefined(av.contractType)
          )
        : undefined
    };
  }

  loadConditionEncounters = async (episodeOfCareId: string | undefined) => {
    if (!episodeOfCareId) return [];

    const encounters =
      this._clinicalRecord.encounterResult?.filter(
        e => e.episodeOfCareId === episodeOfCareId
      ) ?? [];

    this._encounterArray = encounters;

    return encounters;
  };

  get conditionEncounters() {
    return this._encounterArray
      .filter(x => x.patientId === this.clinicalRecord.id)
      .sort((a, b): number => {
        return a.startDateTime < b.startDateTime ? 1 : -1;
      });
  }

  linkEpisodeOfCareToEncounter = async (
    episodeOfCareId: string,
    claimId?: string | undefined
  ) => {
    if (this.core.hasPermissions(Permission.ClaimWrite)) {
      await this.linkClaimEncounter(claimId);
    }

    await updateEncounterEpisodeOfCareId(
      episodeOfCareId,
      this.clinicalRecord,
      this.clinical
    );
    await this.clinicalRecord.updateReasonForVisitFromDiagnosis();
    await this.clinicalRecord.loadPatientEpisodesOfCare();
    await this.clinicalRecord.loadLinkedEncounters();

    await this.setTabs();
  };

  linkClaimAppointment = async (claimId: string | undefined) => {
    if (!claimId) return;

    if (
      !this.clinicalRecord.openEncounter?.calendarEventId ||
      !this.clinicalRecord.openEncounter?.id
    )
      return;

    const claimAppt = await this.getClaimAppointment(claimId);

    if (!claimAppt && this.clinicalRecord.openEncounter?.calendarEventId)
      await this.acc.addClaimAppointment({
        claimId,
        calendarEventId: this.clinicalRecord.openEncounter?.calendarEventId
      });

    const claimEncounter = await this.getClaimEncounter(
      claimId,
      this.clinicalRecord.openEncounter?.id
    );
    if (!claimEncounter) {
      await this.acc.addClaimEncounter({
        claimId,
        encounterId: this.clinicalRecord.openEncounter?.id,
        providerId: this.clinicalRecord.openEncounter?.userId,
        businessRole: this.clinicalRecord.openEncounter?.businessRole
      });
    }

    const claim = await this.clinicalRecord.calendarEvent?.loadClaim();
    await claim?.loadClaimAppointments();
  };

  linkClaimEncounter = async (claimId: string | undefined) => {
    if (!claimId) return;

    if (!this.clinicalRecord.openEncounter?.id) return;

    const claimEncounter = await this.getClaimEncounter(
      claimId,
      this.clinicalRecord.openEncounter?.id
    );
    if (!claimEncounter)
      await this.acc.addClaimEncounter({
        claimId,
        encounterId: this.clinicalRecord.openEncounter?.id,
        providerId: this.clinicalRecord.openEncounter?.userId,
        businessRole: this.clinicalRecord.openEncounter?.businessRole
      });
  };

  unlinkEpisodeOfCareFromEncounter = async () => {
    if (!this.clinicalRecord.openEncounter?.id) return;

    await unlinkEpisodeOfCareFromEncounter(this.clinicalRecord, this._root);

    await this.setTabs();
  };

  unlinkClaimAppt = async (claimId?: string) => {
    if (!claimId || !this.clinicalRecord.openEncounter?.id) return;

    const claimAppt = await this.getClaimAppointment(claimId);
    if (claimAppt) await this.acc.deleteClaimAppointmentDto(claimAppt.id);

    await this.acc.deleteClaimEncounter(
      claimId,
      this.clinicalRecord.openEncounter?.id
    );

    // Is injury tab opend?
    if (!this._injuryTabId) {
      const injuryTab = this.clinicalRecord.patientRecordTab?.allTabs.find(
        t => t.type === AccClinicalTab.Injury
      );
      if (injuryTab) {
        this._injuryTabId = injuryTab?.id;
      }
    }

    // Close Injury tab from the docker
    this.clinicalRecord.patientRecordTab?.removeTab(this._injuryTabId);

    // At this point:
    // has active tab - selected another tab rather than injury one
    // No active tab - the active injury tab has just been removed
    if (!this.clinicalRecord.patientRecordTab?.activeTab) {
      // There is a no active tab, So open Today's note
      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type: PermanentClinicalTab.TodaysNotes
      });
    }
  };

  get hasLinkedCondition(): boolean {
    return !!this.clinicalRecord.openEncounter?.episodeOfCareId;
  }

  getClaimAppointment = async (claimId: string) => {
    const claimAppts = await this.acc.getClaimAppointmentDtos({
      claimIds: [claimId],
      calendarEventIds: this.clinicalRecord.openEncounter?.calendarEventId
        ? [this.clinicalRecord.openEncounter.calendarEventId]
        : undefined
    });

    return claimAppts?.length > 0 ? claimAppts[0] : undefined;
  };

  getClaimEncounter = async (claimId: string, encounterId: string) => {
    const claimEncounters = await this.acc.getClaimEncounters(
      claimId,
      this.clinicalRecord.id
    );

    // Claims can have multiple encounters linked to them so we want to
    // see if the current encounter is already linked
    return claimEncounters?.find(x => x.id === encounterId);
  };

  isLinkedEpisodeOfCare = (episodeOfCareId: string | undefined) => {
    return (
      this.clinicalRecord.openEncounter?.episodeOfCareId === episodeOfCareId ||
      this.clinicalRecord?.episodeOfCare?.id === episodeOfCareId
    );
  };

  isAlliedHealthService = (claim: Claim) =>
    claim.appointmentVisits?.some(
      x => x.contractType === ProviderTypeCode.alliedHealthService
    );

  private emptyModel(emptyMessage: string): ConditionDetailsModel {
    return {
      emptyMessage
    };
  }
}

export interface ConditionDetailsModel {
  episodeOfCare?: EpisodeOfCare;
  diagnosis?: string;
  referralIn?: boolean;
  apptRemaining?: string;
  emptyMessage?: string;
  initialConsultDate?: string;
  appointmentVisits?: AppointmentVisitDto[];
  side?: string;
  injuryDate?: string;
}
